/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { getNamedColor } from '../PRIVATE_colorUtils/colorUtils';
import { DataType } from 'csstype';

export type ColorValueHex = `#${string}`;
export type ColorValue = DataType.NamedColor | ColorValueHex | 'TRANSPARENT';

/**
 * Convert a color keyword to hex value.
 * If the keyword is not valid, return an empty string.
 * @param value
 * @returns
 */
function convertKeywordToHex(value: string): string {
  try {
    return getNamedColor(value);
  } catch {
    return '';
  }
}

/**
 * Returns true if the value is 'transparent'
 * @param value
 * @returns
 */
function isTransparent(value: string | ColorValue): boolean {
  if (value && value.length && value.toUpperCase() === 'TRANSPARENT') {
    return true;
  } else {
    return false;
  }
}

/**
 * Check if the value is a valid named color.
 * @param value
 * @returns
 */
function isNamedColor(value: string): boolean {
  try {
    return getNamedColor(value).length > 0;
  } catch {
    return false;
  }
}

/**
 * Parse a color value and return a hex string or 'TRANSPARENT'.
 * ie, 'blue' => '#0000FF'
 * If not valid keyword, returns ''
 * @param value
 * @returns
 */
export function parseColorValue(value?: string): string {
  if (!value) {
    return '';
  }
  value = value.trim();
  // check for 'transparent' or 'TRANSPARENT' a valid CSS keyword
  if (isTransparent(value)) {
    return value;
  }
  // check for named color
  if (isNamedColor(value)) {
    return convertKeywordToHex(value).toUpperCase();
  }
  // check for string '#xxxxxx' with '#' up to 3, 4,  6, 8 hex digits all valid css colors
  if (/^([#]([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8}))$/.test(value)) {
    return value.toUpperCase();
  }
  return '';
}

/** Get the ColorValue from a string
 */
export function getColorValue(value?: string): ColorValue {
  return parseColorValue(value) as ColorValue;
}

/**
 * Can convert array to all hex values and 'transparent'
 * as desired.
 * @param palette
 * @returns
 */
export function convertPalette(palette: Array<ColorValue>): Array<ColorValue> {
  return palette.map((x) => getColorValue(x));
}

/**
 * More 'transparent' to first position
 * @param palette
 * @returns
 */
export function sortPalette(palette: Array<ColorValue>): Array<ColorValue> {
  const uniqueArray = [...new Set(palette)];
  const p = uniqueArray.map((str) => str?.toLowerCase()) as Array<ColorValue>;
  const i = p.indexOf('transparent');
  switch (i) {
    case 0:
    case -1:
      return p;
    default: {
      const f = p.filter((str) => str?.toLowerCase() !== 'transparent');
      return ['transparent', ...f];
    }
  }
}

/**
 * Return true if the color params represent 2 different colors.
 */
export const colorsAreDifferent = (
  c1: ColorValue | undefined,
  c2: ColorValue | undefined,
  isExpandable = false
) => {
  const c1_empty = !c1 || !c1.length;
  const c2_empty = !c2 || !c2.length;

  // If both colors are empty, return false.
  if (c1_empty && c2_empty) {
    return false;
  }

  // If one is empty and the other isn't, return true.
  if ((c1_empty && !c2_empty) || (!c1_empty && c2_empty)) {
    return true;
  }
  if (
    isExpandable &&
    getExpandedColorValue(c1 as string, true) !== getExpandedColorValue(c2 as string, true)
  ) {
    return true;
  }

  if (c1 !== c2) {
    return true;
  }

  return false;
};

/**
 * Return true if the color value is a valid hex color or 'TRANSPARENT'
 * Valid values to type include
 * - '#xxxxxx'
 * - '#xxxxxxxx'
 * - 'TRANSPARENT'
 * - 'transparent'
 * - named color (e.g. 'blue', 'red', etc.)
 * @param value
 * @returns
 */
export const isValidColorValue = (value: string): boolean => {
  const rgb = value ? parseColorValue(value) : '';
  return rgb ? rgb.length === 7 || rgb.length === 9 || isTransparent(rgb) : false;
};

/**
 * Convert a color value to an 8 digit hex value.
 * If the value is a valid hex color, it will return the value with an alpha channel of 'FF'.
 * If the value is not valid, it will return an empty string.
 * @param value
 * @returns
 */
export const getEightDigitHex = (value: ColorValue): ColorValue => {
  // First check if this is a complete value and return that
  if (isValidColorValue(value)) {
    const retVal = getColorValue(value);
    if (retVal.length === 9) {
      return retVal;
    } else if (retVal.length === 7) {
      return (retVal + 'FF') as ColorValue; // add alpha channel
    }
  }
  return '' as ColorValue; // return empty string for invalid values
};

/**
 * Convert a color value to an 6 digit hex value.
 * If any other alpha, return empty string.
 * @param value
 * @returns
 */
export const getSixDigitHex = (value: ColorValue): ColorValue => {
  // First check if this is a complete value and return that
  if (isValidColorValue(value)) {
    const retVal = getColorValue(value);
    if (/^([#]([0-9A-Fa-f]{6}))$/.test(retVal)) {
      return retVal;
    } else if (/^([#]([0-9A-Fa-f]{8}))$/.test(retVal)) {
      return retVal.substring(0, 7) as ColorValue;
    }
  }
  return '' as ColorValue; // return empty string for invalid values
};

/**
 * Returns true if value is an 8 digit hex value
 * @param value
 * @returns
 */
export const isEightDigitHex = (value: ColorValue): boolean => {
  return /^([#]([0-9A-Fa-f]{8}))$/.test(value);
};

// /**
//  * Returns true if value is an 6 digit hex value
//  * @param value
//  * @returns
//  */
// const isSixDigitHex = (value: ColorValue): boolean => {
//   return (/^([#]([0-9A-Fa-f]{6}))$/.test(value));
// }

/**
 * Returns the intended color value for a partially specified color value.
 * Can request 8 or 6 digit hex rgb return values.  Note that 'transparent' is
 * always returned unconverted as it is a valid css value and requested to stay
 * untouched for ux reasons.  Finds first valid hex substring and applies
 * heuristics.
 *
 * Returns '' if the value is not valid.
 * @param value
 * @returns
 */
export const getExpandedColorValue = (
  value?: string | ColorValue,
  hasEightDigitHex = false
): ColorValue => {
  if (!value) {
    return '' as ColorValue;
  }
  if (isTransparent(value)) {
    return value as ColorValue;
  }
  // Return eight digit hex results
  if (hasEightDigitHex) {
    // First check if this is a complete value and return that
    if (isValidColorValue(value)) {
      return getEightDigitHex(getColorValue(value));
    }
    // Now implement desired behavior for partially specified color values

    // strip leading hash
    const nohash = value[0] === '#' ? value.substring(1) : value;
    // Ux request is to strip all non-hex characters first , upper-case, and then apply heuristics
    const val = nohash?.match(/[0-9A-Fa-f]+/)?.[0].toUpperCase() ?? '';

    // A -> #AAAAAAFF
    if (/^([0-9A-F]{1})$/.test(val)) {
      return ('#' + val + val + val + val + val + val + 'FF') as ColorValue;
    }

    // AB -> #ABABABFF
    if (/^([0-9A-F]{2})$/.test(val)) {
      return ('#' + val[0] + val[1] + val[0] + val[1] + val[0] + val[1] + 'FF') as ColorValue;
    }

    // ABC -> #AABBCCFF
    if (/^([0-9A-F]{3})$/.test(val)) {
      return ('#' + val[0] + val[0] + val[1] + val[1] + val[2] + val[2] + 'FF') as ColorValue;
    }

    // ABCF -> #AABBCCFF
    if (/^([0-9A-F]{4})$/.test(val)) {
      return ('#' +
        val[0] +
        val[0] +
        val[1] +
        val[1] +
        val[2] +
        val[2] +
        val[3] +
        val[3]) as ColorValue;
    }

    // ABCDEF -> #ABCDEFFF
    if (/^([0-9A-F]{6})$/.test(val)) {
      return ('#' + val[0] + val[1] + val[2] + val[3] + val[4] + val[5] + 'FF') as ColorValue;
    }

    // ABCDEFFF -> #ABCDEFFF
    if (/^([0-9A-F]{8})$/.test(val)) {
      return ('#' +
        val[0] +
        val[1] +
        val[2] +
        val[3] +
        val[4] +
        val[5] +
        val[6] +
        val[7]) as ColorValue;
    }
  } else {
    // 6 digit hex path return
    // First check if this is a complete value and return that
    if (isValidColorValue(value)) {
      return getSixDigitHex(getColorValue(value));
    }

    // strip leading hash
    const nohash = value[0] === '#' ? value.substring(1) : value;
    // Ux request is to strip all non-hex characters first , upper-case, and then apply heuristics
    const val = nohash?.match(/[0-9A-Fa-f]+/)?.[0].toUpperCase() ?? '';

    // A -> #AAAAAA
    if (/^([0-9A-F]{1})$/.test(val)) {
      return ('#' + val + val + val + val + val + val) as ColorValue;
    }

    // AB -> #ABABAB
    if (/^([0-9A-F]{2})$/.test(val)) {
      return ('#' + val[0] + val[1] + val[0] + val[1] + val[0] + val[1]) as ColorValue;
    }

    // ABC -> #AABBCC
    if (/^([0-9A-F]{3})$/.test(val)) {
      return ('#' + val[0] + val[0] + val[1] + val[1] + val[2] + val[2]) as ColorValue;
    }

    // ABCDEF -> #ABCDEF
    if (/^([0-9A-F]{6})$/.test(val)) {
      return ('#' + val[0] + val[1] + val[2] + val[3] + val[4] + val[5]) as ColorValue;
    }
  }

  return '' as ColorValue;
};
